import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import ChevronDown from 'react-icons/lib/fa/chevron-down'
import _ from 'lodash'
import { getCurrentAiuwMemos, getCurrentApp, getAIUW } from 'e-submission/apps/selectors'

const Container = styled.div`
  position: relative;
  max-width: 600px;

  @media (max-width: 430px) {
    padding: 15px;
    margin: 20px auto;
  }
`

const AiUwResult = styled.h2`
  color: #2425aa;
  font-size: 22px;
  margin-bottom: 6px;
  font-weight: bold;

  @media (max-width: 430px) {
    font-size: 20px;
    font-weight: bold;
  }
`

const ContentContainer = styled.div`
  background-color: #dcdcea;
  padding: 40px 32px 32px 32px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 430px) {
    border-radius: 12px;
    font-weight: bold;
  }
`

const Text = styled.p`
  font-size: 20px;
  line-height: 1.5;
  color: #2425aa;
  margin: 0;

  @media (max-width: 430px) {
    font-size: 18px;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: #dcdcea;
  padding: 40px 32px 32px 32px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 430px) {
    border-radius: 12px;
    font-weight: bold;
  }
`

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid #f3f3f3; /* Light grey border */
  border-top: 3px solid #2425aa; /* Blue border on top */
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

const AccordionContainer = styled.div`
  max-width: 768px;
  margin: 0 auto;
`

const AccordionItemWrapper = styled.div`
  margin-bottom: 8px;
  overflow: hidden;
  background: #dcdcea;
`

const AccordionButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  text-align: left;
  background-color: #dcdcea;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  font-family: inherit;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #dcdcea;
  }

  @media (max-width: 430px) {
    padding: 16px 0px;
  }
`

const AccordionContent = styled.div`
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  max-height: ${(props) => (props.isOpen ? 'fit-content' : '0')};
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
`

const AccordionContentInner = styled.div`
  padding: 16px 24px;
  background-color: transparent;
  color: #374151;
  line-height: 1.6;

  @media (max-width: 430px) {
    padding: 16px 0px;
  }
`

const ChevronIcon = styled(ChevronDown)`
  width: 20px;
  height: 20px;
  color: #2425aa;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  margin-left: 8px;
`

const AccordionTitle = styled.h2`
  color: #2425aa;
  font-size: 22px;
  margin-bottom: 6px;
  font-weight: bold;
`

const AccordionFooterContainer = styled.div`
  display: flex;
  text-align: left;
  margin-top: 24px;
`

const MemoText = styled(Text)`
  border-bottom: 2px solid #2425aa;
  padding-bottom: 24px;
  text-align: left;
  white-space: pre-line;

  & + & {
    padding-top: 24px;
  }
`

const AccordionItem = ({ title, children, isOpen, onToggle }) => {
  return (
    <AccordionItemWrapper>
      <AccordionButton onClick={onToggle}>
        <AccordionTitle>{title}</AccordionTitle>
        <ChevronIcon isOpen={isOpen} />
      </AccordionButton>
      <AccordionContent isOpen={isOpen}>
        <AccordionContentInner>{children}</AccordionContentInner>
      </AccordionContent>
    </AccordionItemWrapper>
  )
}

// Main Accordion Component
const Accordion = ({ children }) => {
  const [openIndex, setOpenIndex] = useState(null)

  const toggleItem = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index))
  }

  return (
    <AccordionContainer>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            isOpen: openIndex === index,
            onToggle: () => toggleItem(index),
          })
        }
        return child
      })}
    </AccordionContainer>
  )
}

const NotificationCard = ({ resultMessage, remarkMessage, requestStatus, isMRTACategory, isBMInormal }) => {
  const aiuwMemos = useSelector(getCurrentAiuwMemos)
  const aiuwStatus = useSelector((state) => {
    const app = getCurrentApp(state)
    return getAIUW(app)?.status
  })
  const currentMemos =
    isMRTACategory && !isBMInormal
      ? [
          {
            code: 'BMI',
            message:
              'ค่า BMI ไม่อยู่ในเกณฑ์มาตรฐาน บริษัทฯอาจขอให้มีการตรวจสุขภาพเพิ่มเติม หรืออาจพิจารณารับประกันภัยโดยมีการปรับเพิ่มเบี้ยประกัน',
          },
          ...aiuwMemos,
        ]
      : aiuwMemos

  if (aiuwStatus !== 'requesting') {
    return (
      <Container>
        <ContentContainer>
          <AiUwResult>"{resultMessage}"</AiUwResult>
          <Text>{remarkMessage}</Text>
          {!_.isEmpty(currentMemos) && (
            <Accordion>
              <AccordionItem title="บริษัทฯ ได้พิจารณาข้อมูลเบื้องต้นแล้ว ขอให้นำส่งเอกสารเพิ่มเติมดังนี้">
                {currentMemos.map((item) => {
                  return <MemoText dangerouslySetInnerHTML={{ __html: item.message }} />
                })}
                <AccordionFooterContainer>
                  <AiUwResult>
                    ทั้งนี้ บริษัทฯ อาจเรียกขอข้อมูลอื่นๆ เพิ่มเติม หากเห็นว่าจำเป็นต่อการพิจารณาต่อไป
                  </AiUwResult>
                </AccordionFooterContainer>
              </AccordionItem>
            </Accordion>
          )}
        </ContentContainer>
      </Container>
    )
  } else {
    return (
      <Container>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </Container>
    )
  }
}

export default (hoc) => hoc(NotificationCard)
